import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Wallet, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const fmtGs = (n) =>
  new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n || 0);

export default function Pagos() {
  const [pacientes, setPacientes] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const [p, pg] = await Promise.all([api.get("/pacientes"), api.get("/pagos")]);
      setPacientes(p.data);
      setPagos(pg.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const total_pendiente = useMemo(
    () => pacientes.reduce((acc, p) => acc + Math.max(0, p.saldo_pendiente || 0), 0),
    [pacientes]
  );
  const total_pagado = useMemo(() => pagos.reduce((a, p) => a + (p.monto || 0), 0), [pagos]);

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1400px]">
      <PageHeader
        eyebrow="Cobranzas"
        title="Pagos & saldos"
        description="Registro de pagos parciales por paciente y saldo pendiente del tratamiento."
        action={
          <button data-testid="nuevo-pago-btn" onClick={() => setModalOpen(true)} className="btn-primary-pill">
            <Plus className="w-4 h-4 mr-1.5" /> Registrar pago
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <SummaryCard testId="sum-pendiente" label="Saldo pendiente total" value={fmtGs(total_pendiente)} accent="terracotta" />
        <SummaryCard testId="sum-pagado" label="Total cobrado (histórico)" value={fmtGs(total_pagado)} accent="sage" />
        <SummaryCard testId="sum-cant-pagos" label="Pagos registrados" value={pagos.length} accent="neutral" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card-clinic overflow-hidden lg:col-span-3">
          <div className="overline p-5">Saldos por paciente</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/50">
                {["Paciente", "Costo", "Pagado", "Saldo"].map((h, i) => (
                  <th key={i} className="overline text-left px-4 py-3 text-neutral-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pacientes.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-neutral-500 text-sm">
                    No hay pacientes cargados.
                  </td>
                </tr>
              )}
              {pacientes.map((p) => (
                <tr key={p.id} className="border-b border-neutral-200 row-hover">
                  <td className="px-4 py-3">
                    <Link to={`/pacientes/${p.id}`} className="font-semibold text-neutral-900 hover:underline">
                      {p.nombre} {p.apellido}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-neutral-700">{fmtGs(p.costo_tratamiento)}</td>
                  <td className="px-4 py-3 text-[hsl(var(--sage-hover))] font-medium">{fmtGs(p.total_pagado)}</td>
                  <td
                    className={`px-4 py-3 font-semibold ${
                      p.saldo_pendiente > 0 ? "text-[hsl(var(--terracotta))]" : "text-neutral-500"
                    }`}
                  >
                    {fmtGs(p.saldo_pendiente)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-clinic overflow-hidden lg:col-span-2">
          <div className="overline p-5">Pagos recientes</div>
          {pagos.length === 0 ? (
            <div className="text-sm text-neutral-500 py-10 text-center">Sin pagos aún.</div>
          ) : (
            <ul className="divide-y divide-neutral-200">
              {pagos.slice(0, 12).map((pg) => (
                <li key={pg.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-neutral-900 truncate">{pg.paciente_nombre}</div>
                    <div className="text-xs text-neutral-500 capitalize">
                      {pg.fecha} · {pg.metodo}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-[hsl(var(--sage-hover))]">{fmtGs(pg.monto)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {modalOpen && (
        <PagoModal
          pacientes={pacientes}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent = "neutral", testId }) {
  const accents = {
    sage: "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]",
    terracotta: "bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta-hover))]",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  return (
    <div className="card-clinic p-6" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
        <Wallet className="w-5 h-5" />
      </div>
      <div className="overline mt-5 mb-1">{label}</div>
      <div className="text-2xl font-bold tracking-tight text-neutral-900">{value}</div>
    </div>
  );
}

function PagoModal({ onClose, onSaved, pacientes }) {
  const [paciente_id, setPacienteId] = useState(pacientes[0]?.id || "");
  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState("efectivo");
  const [concepto, setConcepto] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const save = async () => {
    const m = Number(monto);
    if (!paciente_id || !m || m <= 0) {
      toast.error("Elegí paciente e ingresá un monto válido");
      return;
    }
    setLoading(true);
    try {
      await api.post("/pagos", { paciente_id, monto: m, metodo, concepto, fecha });
      toast.success("Pago registrado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg border border-neutral-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="overline">Nuevo</div>
            <h3 className="text-lg font-bold tracking-tight">Registrar pago</h3>
          </div>
          <button data-testid="pago-modal-close" onClick={onClose} className="btn-ghost-pill p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Paciente</label>
            <select
              data-testid="pago-paciente"
              className="input-clinic"
              value={paciente_id}
              onChange={(e) => setPacienteId(e.target.value)}
            >
              <option value="">Seleccionar…</option>
              {pacientes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} {p.apellido}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Monto</label>
              <input
                data-testid="pago-monto"
                type="number"
                className="input-clinic"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Fecha</label>
              <input
                data-testid="pago-fecha"
                type="date"
                className="input-clinic"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Método</label>
            <select
              data-testid="pago-metodo"
              className="input-clinic"
              value={metodo}
              onChange={(e) => setMetodo(e.target.value)}
            >
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Concepto</label>
            <input
              data-testid="pago-concepto"
              className="input-clinic"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Opcional"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50/50 rounded-b-2xl">
          <button onClick={onClose} className="btn-outline-pill">
            Cancelar
          </button>
          <button data-testid="pago-guardar" onClick={save} disabled={loading} className="btn-primary-pill">
            {loading ? "Guardando…" : "Registrar pago"}
          </button>
        </div>
      </div>
    </div>
  );
}
